/**
 * Client twin of backend services/search_text.py: the same fold, the
 * same term grammar (bare word = prefix, quoted = exact word / phrase),
 * so the description in the listing drawer can highlight exactly what
 * the server matched on. Folding is per UTF-16 unit, so hit positions
 * in the folded copy map 1:1 onto the original string.
 */
const FOLD: Record<string, string> = {
  "ə": "e", "ç": "c", "ş": "s", "ğ": "g", "ö": "o", "ü": "u", "ı": "i",
};

export function foldChar(ch: string): string {
  if (ch === "İ" || ch === "I") return "i";
  let c = ch.toLowerCase();
  if (c.length !== 1) c = c[0];
  return FOLD[c] ?? c;
}

export function foldKeepIndex(s: string): string {
  let out = "";
  for (let i = 0; i < s.length; i++) out += foldChar(s[i]);
  return out;
}

export type SearchTerm = { words: string[]; exact: boolean };

const WORD = /[\p{L}\p{N}]+/gu;
const QUOTED = /["«»“”„]([^"«»“”„]+)["«»“”„]/g;
const MAX_TERMS = 6;

export function parseSearchTerms(q: string | null | undefined): SearchTerm[] {
  if (!q) return [];
  q = q.slice(0, 120);
  const terms: SearchTerm[] = [];
  const seen = new Set<string>();
  const add = (words: string[], exact: boolean) => {
    const key = `${exact ? "!" : ""}${words.join(" ")}`;
    if (words.length && !seen.has(key) && terms.length < MAX_TERMS) {
      seen.add(key);
      terms.push({ words, exact });
    }
  };
  const rest: string[] = [];
  let pos = 0;
  for (const m of q.matchAll(QUOTED)) {
    rest.push(q.slice(pos, m.index));
    pos = (m.index ?? 0) + m[0].length;
    add((foldKeepIndex(m[1]).match(WORD) ?? []).slice(0, MAX_TERMS), true);
  }
  rest.push(q.slice(pos));
  for (const tok of foldKeepIndex(rest.join(" ")).match(WORD) ?? []) {
    if (tok.length >= 2) add([tok], false);
  }
  return terms;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function termRegex(t: SearchTerm): RegExp {
  const L = "\\p{L}\\p{N}";
  if (t.exact) {
    const body = t.words.map(esc).join(`[^${L}]+`);
    return new RegExp(`(?<![${L}])(${body})(?![${L}])`, "gu");
  }
  return new RegExp(`(?<![${L}])(${esc(t.words[0])}[${L}]*)`, "gu");
}

export type HighlightSegment = { text: string; hit: boolean };

/** Split ``text`` into plain and hit segments for the given query. */
export function highlightSegments(text: string, q: string | null | undefined): HighlightSegment[] {
  const terms = parseSearchTerms(q);
  if (!text || terms.length === 0) return [{ text, hit: false }];
  const folded = foldKeepIndex(text);
  const spans: [number, number][] = [];
  for (const t of terms) {
    for (const m of folded.matchAll(termRegex(t))) {
      const a = m.index ?? 0;
      spans.push([a, a + m[1].length]);
    }
  }
  if (spans.length === 0) return [{ text, hit: false }];
  spans.sort((x, y) => x[0] - y[0]);
  const merged: [number, number][] = [];
  for (const [a, b] of spans) {
    const last = merged[merged.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else merged.push([a, b]);
  }
  const out: HighlightSegment[] = [];
  let pos = 0;
  for (const [a, b] of merged) {
    if (a > pos) out.push({ text: text.slice(pos, a), hit: false });
    out.push({ text: text.slice(a, b), hit: true });
    pos = b;
  }
  if (pos < text.length) out.push({ text: text.slice(pos), hit: false });
  return out;
}
